import { useCallback, useEffect, useState } from "react";
import { Landmark, Plus, X, Sparkles, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { fmtNum } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";

const gbp = (v) => (v == null ? "—" : `£${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const IsaTracker = () => {
  const [isa, setIsa] = useState(null);
  const [form, setForm] = useState({ amount: "", date: new Date().toISOString().slice(0, 10), note: "" });

  const load = useCallback(() => api.get("/tax/isa").then((r) => setIsa(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.amount || Number(form.amount) <= 0) return;
    try {
      await api.post("/tax/isa", { amount: Number(form.amount), date: form.date, note: form.note });
      setForm({ ...form, amount: "", note: "" });
      toast.success("Contribution added");
      load();
    } catch (e) {
      toast.error(apiErrorDetail(e));
    }
  };

  const remove = async (id) => {
    await api.delete(`/tax/isa/${id}`);
    load();
  };

  if (!isa) return <Skeleton className="h-64 rounded-sm bg-card" />;
  const usedPct = Math.min(100, (isa.contributed_this_year / isa.allowance) * 100);

  return (
    <div className="widget p-6" data-testid="isa-tracker">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h2 className="font-heading font-bold text-lg text-white tracking-tight">ISA allowance tracker</h2>
        <span className="font-mono text-xs text-muted-foreground">
          Tax year {isa.tax_year_label} · ends {isa.tax_year_end} ({isa.days_until_year_end} days left)
        </span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Log what you've paid into ISAs this tax year against the £{fmtNum(isa.allowance, 0)} allowance.</p>

      <Progress value={usedPct} className="h-3" data-testid="isa-progress-bar" />
      <div className="flex justify-between font-mono text-xs mt-2 mb-5">
        <span className={isa.over_allowance ? "text-loss" : "text-white"} data-testid="isa-used-amount">
          {gbp(isa.contributed_this_year)} used {isa.over_allowance && "· OVER ALLOWANCE"}
        </span>
        <span className="text-gain" data-testid="isa-remaining-amount">{gbp(isa.remaining)} remaining</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
        <div className="space-y-1.5">
          <Label className="overline-label">Amount £</Label>
          <Input type="number" min="0" step="any" className="rounded-sm bg-background font-mono" data-testid="isa-amount-input"
            value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label className="overline-label">Date</Label>
          <Input type="date" className="rounded-sm bg-background font-mono" data-testid="isa-date-input"
            value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label className="overline-label">Note</Label>
          <Input placeholder="optional" className="rounded-sm bg-background" data-testid="isa-note-input"
            value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </div>
        <Button onClick={add} disabled={!form.amount || Number(form.amount) <= 0}
          className="rounded-sm bg-primary hover:bg-primary/85 text-white gap-1.5" data-testid="isa-add-button">
          <Plus className="w-4 h-4" /> Add
        </Button>
      </div>

      {isa.contributions.length > 0 && (
        <div className="mt-5 space-y-1" data-testid="isa-contributions-list">
          {isa.contributions.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-3 py-2 even:bg-white/[0.02] text-sm font-mono"
              data-testid={`isa-contribution-${c.id}`}>
              <span className="text-gray-300 w-28">{c.date}</span>
              <span className="text-white w-28">{gbp(c.amount)}</span>
              <span className="flex-1 text-xs text-muted-foreground font-body truncate">{c.note}</span>
              <button onClick={() => remove(c.id)} data-testid={`isa-delete-${c.id}`}
                className="text-muted-foreground/40 hover:text-loss transition-colors duration-150"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Estimator = ({ title, subtitle, amountLabel, bands, endpoint, amountKey, testPrefix }) => {
  const [amount, setAmount] = useState("");
  const [band, setBand] = useState(bands[0].value);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(endpoint, { [amountKey]: Number(amount || 0), band });
      setResult(data);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="widget p-6" data-testid={`${testPrefix}-estimator`}>
      <h2 className="font-heading font-bold text-lg text-white tracking-tight mb-1">{title}</h2>
      <p className="text-sm text-muted-foreground mb-4">{subtitle}</p>
      <div className="grid grid-cols-2 gap-2 items-end mb-3">
        <div className="space-y-1.5">
          <Label className="overline-label">{amountLabel}</Label>
          <Input type="number" min="0" step="any" className="rounded-sm bg-background font-mono" data-testid={`${testPrefix}-amount-input`}
            value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="overline-label">Tax band</Label>
          <Select value={band} onValueChange={setBand}>
            <SelectTrigger className="rounded-sm bg-background h-10" data-testid={`${testPrefix}-band-select`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-sm">
              {bands.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button onClick={run} disabled={loading || amount === ""} className="rounded-sm bg-primary hover:bg-primary/85 text-white w-full"
        data-testid={`${testPrefix}-calculate-button`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Estimate"}
      </Button>
      {result && (
        <div className="mt-4 space-y-1.5 font-mono text-sm fade-up" data-testid={`${testPrefix}-result`}>
          <div className="flex justify-between"><span className="text-muted-foreground">Tax-free portion used</span>
            <span className="text-gray-300">{gbp(result.allowance_used ?? result.exempt_used)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Taxable</span>
            <span className="text-gray-300">{gbp(result.taxable)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Rate</span>
            <span className="text-gray-300">{result.rate_pct}%</span></div>
          <div className="flex justify-between border-t border-border pt-1.5"><span className="text-white">Estimated tax</span>
            <span className="text-loss" data-testid={`${testPrefix}-tax-amount`}>{gbp(result.estimated_tax)}</span></div>
          <div className="flex justify-between"><span className="text-white">Net after tax</span>
            <span className="text-gain">{gbp(result.net_after_tax)}</span></div>
          <p className="text-[11px] text-muted-foreground font-body pt-2">{result.note}</p>
        </div>
      )}
    </div>
  );
};

const AskTax = () => {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null);

  const ask = async () => {
    if (!question.trim()) return;
    setLoading(true);
    setAnswer(null);
    try {
      const { data } = await api.post("/tax/ask", { question: question.trim() });
      setAnswer(data.answer);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="widget p-6 bg-blue-950/20 border-primary/20" data-testid="tax-ask-panel">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-primary" />
        <h2 className="font-heading font-bold text-lg text-white tracking-tight">Ask about the rules</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Explains general UK ISA / dividend / CGT rules only — it will refuse personalised tax advice.
      </p>
      <div className="flex gap-2">
        <Input value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="e.g. What happens if I exceed my ISA allowance?" className="rounded-sm bg-background" data-testid="tax-question-input" />
        <Button onClick={ask} disabled={loading || !question.trim()}
          className="rounded-sm bg-primary hover:bg-primary/85 text-white shrink-0" data-testid="tax-ask-button">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ask"}
        </Button>
      </div>
      {answer && (
        <div className="mt-4 fade-up" data-testid="tax-answer">
          {answer.split("\n").map((l, i) => <p key={i} className="text-sm text-gray-100 leading-relaxed">{l || "\u00a0"}</p>)}
        </div>
      )}
    </div>
  );
};

export default function TaxAssistant() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/tax/config").then((r) => setConfig(r.data));
  }, []);

  return (
    <div className="space-y-6 fade-up">
      <div>
        <div className="overline-label mb-2">UK investor toolkit</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white flex items-center gap-3">
          ISA &amp; Tax Assistant <Landmark className="w-8 h-8 text-primary" />
        </h1>
      </div>

      <div className="border border-yellow-500/40 bg-yellow-500/10 rounded-sm px-4 py-3 flex items-start gap-3" data-testid="tax-disclaimer-banner">
        <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
        <p className="text-sm text-yellow-200/90">
          <span className="font-medium text-yellow-500">Not tax advice.</span>{" "}
          {config?.disclaimer || "This is educational information, not tax advice."}{" "}
          All figures are for tax year {config?.tax_year_label || "…"}.
        </p>
      </div>

      {config && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tax-figures-grid">
          <div className="widget p-4"><div className="overline-label">ISA allowance</div>
            <div className="font-mono text-xl text-white mt-2">£{fmtNum(config.isa_allowance, 0)}</div></div>
          <div className="widget p-4"><div className="overline-label">Dividend allowance</div>
            <div className="font-mono text-xl text-white mt-2">£{fmtNum(config.dividend_allowance, 0)}</div></div>
          <div className="widget p-4"><div className="overline-label">Dividend rates</div>
            <div className="font-mono text-sm text-white mt-2">{config.dividend_rates.basic}% / {config.dividend_rates.higher}% / {config.dividend_rates.additional}%</div>
            <div className="text-[10px] text-muted-foreground font-mono mt-1">basic / higher / additional</div></div>
          <div className="widget p-4"><div className="overline-label">CGT exempt + rates</div>
            <div className="font-mono text-sm text-white mt-2">£{fmtNum(config.cgt_annual_exempt, 0)} · {config.cgt_rates.basic}% / {config.cgt_rates.higher}%</div>
            <div className="text-[10px] text-muted-foreground font-mono mt-1">annual exemption · basic / higher</div></div>
        </div>
      )}

      <IsaTracker />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Estimator title="Dividend tax estimator" subtitle="Estimate tax on dividends held outside an ISA."
          amountLabel="Annual dividend income £" endpoint="/tax/dividend-estimate" amountKey="dividend_income" testPrefix="dividend"
          bands={[
            { value: "basic", label: `Basic rate (${config?.dividend_rates.basic ?? "…"}%)` },
            { value: "higher", label: `Higher rate (${config?.dividend_rates.higher ?? "…"}%)` },
            { value: "additional", label: `Additional rate (${config?.dividend_rates.additional ?? "…"}%)` },
          ]} />
        <Estimator title="Capital gains tax estimator" subtitle="Estimate CGT on share gains realised outside an ISA."
          amountLabel="Realised gains £" endpoint="/tax/cgt-estimate" amountKey="gains" testPrefix="cgt"
          bands={[
            { value: "basic", label: `Basic rate (${config?.cgt_rates.basic ?? "…"}%)` },
            { value: "higher", label: `Higher rate (${config?.cgt_rates.higher ?? "…"}%)` },
          ]} />
      </div>

      <AskTax />
    </div>
  );
}
